import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Search, Plus, Edit, Trash2, Crown, Loader2, RefreshCw, EyeOff, Wrench, Sparkles } from 'lucide-react';
import { apiService, SubscriptionPlan } from '../../services/api';
import { PlanToolsDialog } from './PlanToolsDialog';
import { PlanFeaturesDialog } from './PlanFeaturesDialog';
import { PlanCapabilitiesPreview } from './PlanCapabilitiesPreview';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';

type BillingPeriod = 'weekly' | 'monthly' | 'yearly';

interface FormData {
  name: string;
  description: string;
  billingPeriod: BillingPeriod;
  priceInCents: number;
  currency: string;
  pricePerMinuteInCents: number;
  pricePerChatInCents: number;
  includedPhoneNumbers: number | null;
  phoneSetupFeeInCents: number;
  phoneMonthlyFeeInCents: number;
  externalForwardingEnabled: boolean;
  pricePerExternalForwardingInCents: number;
  maxExternalForwardings: number | null;
  maxChatbots: number | null;
  maxVoicebots: number | null;
  maxCallMinutes: number | null;
  freeInclusiveMinutes: number | null;
  maxConversations: number | null;
  maxTestRuns: number | null;
  maxPhoneNumbers: number | null;
  maxKnowledgeDirectories: number | null;
  sortIx: number;
  isActive: boolean;
  isPrivate: boolean;
}

interface FormErrors {
  name?: string;
  priceInCents?: string;
  maxExternalForwardings?: string;
  maxChatbots?: string;
  maxVoicebots?: string;
  maxCallMinutes?: string;
  maxConversations?: string;
  maxTestRuns?: string;
  maxPhoneNumbers?: string;
  maxKnowledgeDirectories?: string;
}

type LimitFieldKey = 'maxChatbots' | 'maxVoicebots' | 'maxCallMinutes' | 'maxConversations' | 'maxTestRuns' | 'maxPhoneNumbers' | 'maxKnowledgeDirectories' | 'maxExternalForwardings';

export function SubscriptionPlansPage() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [toolsDrawerPlan, setToolsDrawerPlan] = useState<SubscriptionPlan | null>(null);
  const [featuresDrawerPlan, setFeaturesDrawerPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    billingPeriod: 'monthly',
    priceInCents: 0,
    currency: 'EUR',
    pricePerMinuteInCents: 0,
    pricePerChatInCents: 0,
    includedPhoneNumbers: 0,
    phoneSetupFeeInCents: 0,
    phoneMonthlyFeeInCents: 0,
    externalForwardingEnabled: false,
    pricePerExternalForwardingInCents: 0,
    maxExternalForwardings: null,
    maxChatbots: null,
    maxVoicebots: null,
    maxCallMinutes: null,
    freeInclusiveMinutes: null,
    maxConversations: null,
    maxTestRuns: null,
    maxPhoneNumbers: null,
    maxKnowledgeDirectories: null,
    sortIx: 0,
    isActive: true,
    isPrivate: false,
  });

  const loadPlans = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getSubscriptionPlans(true);
      setPlans(data);
    } catch (error) {
      console.error('Failed to load subscription plans:', error);
      toast.error('Subscription Plans konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const filteredPlans = plans
    .filter(plan =>
      plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (plan.description && plan.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => a.sortIx - b.sortIx
  );

  const formatPrice = (priceInCents: number, currency: string) => {
    const price = priceInCents / 100;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const formatLimitValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'Unbegrenzt';
    return value.toLocaleString('de-DE');
  };

  const getIntervalLabel = (billingPeriod: string) => {
    const labels: Record<string, string> = {
      weekly: 'Wöchentlich',
      monthly: 'Monatlich',
      yearly: 'Jährlich'
    };
    return labels[billingPeriod] || billingPeriod;
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name ist ein Pflichtfeld.';
    } else if (formData.name.length > 255) {
      errors.name = 'Name darf maximal 255 Zeichen lang sein.';
    }

    if (formData.priceInCents < 0) {
      errors.priceInCents = 'Preis darf nicht negativ sein.';
    }

    const limitFields: { key: LimitFieldKey; label: string }[] = [
      { key: 'maxChatbots', label: 'Max. Chatbots' },
      { key: 'maxVoicebots', label: 'Max. Voicebots' },
      { key: 'maxCallMinutes', label: 'Max. Minuten/Monat' },
      { key: 'maxConversations', label: 'Max. Gespräche/Monat' },
      { key: 'maxTestRuns', label: 'Max. Testläufe' },
      { key: 'maxPhoneNumbers', label: 'Max. Telefonnummern' },
      { key: 'maxKnowledgeDirectories', label: 'Max. Knowledge Base' },
      { key: 'maxExternalForwardings', label: 'Max. Ext. Weiterleitungen' },
    ];

    for (const field of limitFields) {
      const value = formData[field.key];
      if (value !== null && value < 1) {
        errors[field.key] = `${field.label} muss mindestens 1 sein.`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormErrors({});
    setFormData({
      name: plan.name,
      description: plan.description || '',
      billingPeriod: plan.billingPeriod,
      priceInCents: plan.priceInCents,
      currency: plan.currency,
      pricePerMinuteInCents: plan.pricePerMinuteInCents ?? 0,
      pricePerChatInCents: plan.pricePerChatInCents ?? 0,
      includedPhoneNumbers: plan.includedPhoneNumbers ?? 0,
      phoneSetupFeeInCents: plan.phoneSetupFeeInCents ?? 0,
      phoneMonthlyFeeInCents: plan.phoneMonthlyFeeInCents ?? 0,
      externalForwardingEnabled: plan.externalForwardingEnabled ?? false,
      pricePerExternalForwardingInCents: plan.pricePerExternalForwardingInCents ?? 0,
      maxExternalForwardings: plan.maxExternalForwardings ?? null,
      maxChatbots: plan.maxChatbots ?? null,
      maxVoicebots: plan.maxVoicebots ?? null,
      maxCallMinutes: plan.maxCallMinutes ?? null,
      freeInclusiveMinutes: plan.freeInclusiveMinutes ?? null,
      maxConversations: plan.maxConversations ?? null,
      maxTestRuns: plan.maxTestRuns ?? null,
      maxPhoneNumbers: plan.maxPhoneNumbers ?? null,
      maxKnowledgeDirectories: plan.maxKnowledgeDirectories ?? null,
      sortIx: plan.sortIx ?? 0,
      isActive: plan.isActive,
      isPrivate: plan.isPrivate ?? false,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const planData = {
        name: formData.name,
        description: formData.description || undefined,
        billingPeriod: formData.billingPeriod,
        priceInCents: formData.priceInCents,
        currency: formData.currency,
        pricePerMinuteInCents: formData.pricePerMinuteInCents,
        pricePerChatInCents: formData.pricePerChatInCents,
        includedPhoneNumbers: formData.includedPhoneNumbers,
        phoneSetupFeeInCents: formData.phoneSetupFeeInCents,
        phoneMonthlyFeeInCents: formData.phoneMonthlyFeeInCents,
        externalForwardingEnabled: formData.externalForwardingEnabled,
        pricePerExternalForwardingInCents: formData.pricePerExternalForwardingInCents,
        maxExternalForwardings: formData.maxExternalForwardings,
        maxChatbots: formData.maxChatbots,
        maxVoicebots: formData.maxVoicebots,
        maxCallMinutes: formData.maxCallMinutes,
        freeInclusiveMinutes: formData.freeInclusiveMinutes,
        maxConversations: formData.maxConversations,
        maxTestRuns: formData.maxTestRuns,
        maxPhoneNumbers: formData.maxPhoneNumbers,
        maxKnowledgeDirectories: formData.maxKnowledgeDirectories,
        sortIx: formData.sortIx,
        isActive: formData.isActive,
        isPrivate: formData.isPrivate,
      };

      if (editingPlan) {
        const updated = await apiService.updateSubscriptionPlan(editingPlan.id, planData);
        setPlans(plans.map(p => p.id === editingPlan.id ? updated : p));
        toast.success('Plan wurde erfolgreich aktualisiert.');
      } else {
        const created = await apiService.createSubscriptionPlan(planData);
        setPlans([...plans, created]);
        toast.success('Plan wurde erfolgreich erstellt.');
      }
      closeDialog();
    } catch (error) {
      console.error('Failed to save subscription plan:', error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteDialog = (plan: SubscriptionPlan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!planToDelete) return;

    setIsDeleting(true);
    try {
      await apiService.deleteSubscriptionPlan(planToDelete.id);
      setPlans(plans.filter(p => p.id !== planToDelete.id));
      toast.success('Plan wurde erfolgreich gelöscht.');
    } catch (error) {
      console.error('Failed to delete subscription plan:', error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  const toggleActive = async (id: string) => {
    const plan = plans.find(p => p.id === id);
    if (!plan) return;

    try {
      const updated = await apiService.updateSubscriptionPlan(id, { isActive: !plan.isActive });
      setPlans(plans.map(p => p.id === id ? updated : p));
    } catch (error) {
      console.error('Failed to toggle plan status:', error);
      toast.error(extractErrorMessage(error));
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingPlan(null);
    setFormErrors({});
    setFormData({
      name: '',
      description: '',
      billingPeriod: 'monthly',
      priceInCents: 0,
      currency: 'EUR',
      pricePerMinuteInCents: 0,
      pricePerChatInCents: 0,
      includedPhoneNumbers: 0,
      phoneSetupFeeInCents: 0,
      phoneMonthlyFeeInCents: 0,
      externalForwardingEnabled: false,
      pricePerExternalForwardingInCents: 0,
      maxExternalForwardings: null,
      maxChatbots: null,
      maxVoicebots: null,
      maxCallMinutes: null,
      freeInclusiveMinutes: null,
      maxConversations: null,
      maxTestRuns: null,
      maxPhoneNumbers: null,
      maxKnowledgeDirectories: null,
      sortIx: 0,
      isActive: true,
      isPrivate: false,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Abonnement Pläne</h1>
          <p className="text-gray-600">Verwaltung der verfügbaren Tarife und Preispläne</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={loadPlans}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button onClick={() => { setEditingPlan(null); setFormErrors({}); setIsDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Plan
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Pläne suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredPlans.map((plan) => (
          <Card key={plan.id} className={`relative hover:shadow-lg transition-shadow ${plan.isUnlimited ? 'ring-2 ring-blue-500' : ''}`}>
            {plan.isUnlimited && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-blue-500 text-white">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              </div>
            )}

            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-between mb-2">
                <Switch
                  checked={plan.isActive}
                  onCheckedChange={() => toggleActive(plan.id)}
                />
                <div className="w-8" /> {/* Spacer for alignment */}
              </div>

              <CardTitle className="text-xl">{plan.name}</CardTitle>
              <CardDescription className="text-sm">{plan.description}</CardDescription>

              <div className="mt-4">
                <div className="text-3xl font-bold text-gray-900">
                  {formatPrice(plan.priceInCents, plan.currency)}
                </div>
                <div className="text-sm text-gray-500">
                  {getIntervalLabel(plan.billingPeriod)}
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="space-y-1 text-sm border-b pb-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Pro Minute:</span>
                  <span className="font-medium">{formatPrice(plan.pricePerMinuteInCents, plan.currency)}</span>
                </div>
                {plan.freeInclusiveMinutes != null && plan.freeInclusiveMinutes > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Inklusivminuten:</span>
                    <span className="font-medium">{plan.freeInclusiveMinutes.toLocaleString('de-DE')}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Pro Chat:</span>
                  <span className="font-medium">{formatPrice(plan.pricePerChatInCents, plan.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Tel. Einrichtung:</span>
                  <span className="font-medium">{formatPrice(plan.phoneSetupFeeInCents, plan.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Tel. monatl.:</span>
                  <span className="font-medium">{formatPrice(plan.phoneMonthlyFeeInCents, plan.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Inkl. Nummern:</span>
                  <span className="font-medium">{plan.includedPhoneNumbers == null ? 'Unbegrenzt' : plan.includedPhoneNumbers}</span>
                </div>
                {plan.externalForwardingEnabled && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Pro Weiterleitung:</span>
                    <span className="font-medium">{formatPrice(plan.pricePerExternalForwardingInCents, plan.currency)}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Chatbots:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxChatbots)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Voicebots:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxVoicebots)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Minuten/Monat:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxCallMinutes)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Gespräche/Monat:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxConversations)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Testläufe:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxTestRuns)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Telefonnummern:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxPhoneNumbers)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Knowledge Base:</span>
                  <span className="font-medium">{formatLimitValue(plan.maxKnowledgeDirectories)}</span>
                </div>
                {plan.externalForwardingEnabled && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Ext. Weiterleitungen:</span>
                    <span className="font-medium">{formatLimitValue(plan.maxExternalForwardings)}</span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Sortierung:</span>
                  <span className="font-medium">{plan.sortIx}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Status:</span>
                  <Badge variant={plan.isActive ? 'default' : 'secondary'}>
                    {plan.isActive ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </div>
                {plan.isPrivate && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Sichtbarkeit:</span>
                    <Badge variant="outline">
                      <EyeOff className="w-3 h-3 mr-1" />
                      Privat
                    </Badge>
                  </div>
                )}
              </div>

              <div className="border-t pt-3">
                <PlanCapabilitiesPreview planId={plan.id} />
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(plan)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setToolsDrawerPlan(plan)}
                  title="Tools verwalten"
                >
                  <Wrench className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFeaturesDrawerPlan(plan)}
                  title="Features verwalten"
                >
                  <Sparkles className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openDeleteDialog(plan)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPlans.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Keine Pläne gefunden.</p>
            <Button className="mt-4" onClick={() => { setEditingPlan(null); setFormErrors({}); setIsDialogOpen(true); }}>
              Ersten Plan erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Plan bearbeiten' : 'Neuen Plan erstellen'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie die Details und Limits für den Abonnement-Plan
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({...formData, name: e.target.value});
                    if (formErrors.name) setFormErrors({...formErrors, name: undefined});
                  }}
                  placeholder="z.B. Professional"
                  maxLength={255}
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-xs text-red-500">{formErrors.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingPeriod">Abrechnungsintervall</Label>
                <Select value={formData.billingPeriod} onValueChange={(value) => setFormData({...formData, billingPeriod: value as BillingPeriod})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Intervall wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Wöchentlich</SelectItem>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                    <SelectItem value="yearly">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschreibung des Plans..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priceInCents">Preis in Cent *</Label>
                <Input
                  id="priceInCents"
                  type="number"
                  min={0}
                  value={formData.priceInCents}
                  onChange={(e) => {
                    setFormData({...formData, priceInCents: parseInt(e.target.value) || 0});
                    if (formErrors.priceInCents) setFormErrors({...formErrors, priceInCents: undefined});
                  }}
                  placeholder="2999 (= 29.99 EUR)"
                  className={formErrors.priceInCents ? 'border-red-500' : ''}
                />
                {formErrors.priceInCents ? (
                  <p className="text-xs text-red-500">{formErrors.priceInCents}</p>
                ) : (
                  <p className="text-xs text-gray-500">
                    Aktuell: {formatPrice(formData.priceInCents, formData.currency)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Währung</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Währung wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Variable Preise</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pricePerMinuteInCents">Preis pro Minute (Cent)</Label>
                  <Input
                    id="pricePerMinuteInCents"
                    type="number"
                    min={0}
                    value={formData.pricePerMinuteInCents}
                    onChange={(e) => setFormData({...formData, pricePerMinuteInCents: parseInt(e.target.value) || 0})}
                    placeholder="16 (= 0.16 EUR)"
                  />
                  <p className="text-xs text-gray-500">
                    Aktuell: {formatPrice(formData.pricePerMinuteInCents, formData.currency)}/Min
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="freeInclusiveMinutes">Inklusivminuten / Periode</Label>
                  <Input
                    id="freeInclusiveMinutes"
                    type="number"
                    min={0}
                    value={formData.freeInclusiveMinutes ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({...formData, freeInclusiveMinutes: val === '' ? null : parseInt(val) || 0});
                    }}
                    placeholder="z.B. 150 (leer = keine)"
                  />
                  <p className="text-xs text-gray-500">
                    Im Basispreis enthaltene Minuten (leer = keine Inklusivminuten)
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pricePerChatInCents">Preis pro Chat (Cent)</Label>
                  <Input
                    id="pricePerChatInCents"
                    type="number"
                    min={0}
                    value={formData.pricePerChatInCents}
                    onChange={(e) => setFormData({...formData, pricePerChatInCents: parseInt(e.target.value) || 0})}
                    placeholder="5 (= 0.05 EUR)"
                  />
                  <p className="text-xs text-gray-500">
                    Aktuell: {formatPrice(formData.pricePerChatInCents, formData.currency)}/Chat
                  </p>
                </div>
              </div>

              <h4 className="text-sm font-medium text-gray-700 mt-4">Externe Anrufweiterleitung</h4>
              <div className="flex items-center space-x-2 mb-2">
                <Switch
                  id="externalForwardingEnabled"
                  checked={formData.externalForwardingEnabled}
                  onCheckedChange={(checked) => setFormData({...formData, externalForwardingEnabled: checked})}
                />
                <Label htmlFor="externalForwardingEnabled">Ext. Anrufweiterleitung aktiv</Label>
              </div>
              {formData.externalForwardingEnabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pricePerExternalForwardingInCents">Preis pro Weiterleitung (Cent)</Label>
                    <Input
                      id="pricePerExternalForwardingInCents"
                      type="number"
                      min={0}
                      value={formData.pricePerExternalForwardingInCents}
                      onChange={(e) => setFormData({...formData, pricePerExternalForwardingInCents: parseInt(e.target.value) || 0})}
                      placeholder="50 (= 0.50 EUR)"
                    />
                    <p className="text-xs text-gray-500">
                      Aktuell: {formatPrice(formData.pricePerExternalForwardingInCents, formData.currency)}/Weiterleitung
                    </p>
                  </div>
                </div>
              )}

              <h4 className="text-sm font-medium text-gray-700 mt-4">Telefonnummern-Preise</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneSetupFeeInCents">Einrichtungsgebühr (Cent)</Label>
                  <Input
                    id="phoneSetupFeeInCents"
                    type="number"
                    min={0}
                    value={formData.phoneSetupFeeInCents}
                    onChange={(e) => setFormData({...formData, phoneSetupFeeInCents: parseInt(e.target.value) || 0})}
                    placeholder="500 (= 5.00 EUR)"
                  />
                  <p className="text-xs text-gray-500">
                    Aktuell: {formatPrice(formData.phoneSetupFeeInCents, formData.currency)} einmalig
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneMonthlyFeeInCents">Monatsgebühr (Cent)</Label>
                  <Input
                    id="phoneMonthlyFeeInCents"
                    type="number"
                    min={0}
                    value={formData.phoneMonthlyFeeInCents}
                    onChange={(e) => setFormData({...formData, phoneMonthlyFeeInCents: parseInt(e.target.value) || 0})}
                    placeholder="200 (= 2.00 EUR)"
                  />
                  <p className="text-xs text-gray-500">
                    Aktuell: {formatPrice(formData.phoneMonthlyFeeInCents, formData.currency)}/Monat
                  </p>
                </div>
              </div>
              <div className="space-y-2 mt-2">
                <Label htmlFor="includedPhoneNumbers">Inkludierte Telefonnummern</Label>
                <Input
                  id="includedPhoneNumbers"
                  type="number"
                  min={0}
                  value={formData.includedPhoneNumbers ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({...formData, includedPhoneNumbers: val === '' ? null : parseInt(val) || 0});
                  }}
                  placeholder="0 = keine, leer = unbegrenzt"
                />
                <p className="text-xs text-gray-500">
                  Anzahl kostenloser Nummern im Plan (0 = keine inkludiert, leer = alle unbegrenzt)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sortIx">Sortierung (Plan-Hierarchie)</Label>
                <Input
                  id="sortIx"
                  type="number"
                  min={0}
                  value={formData.sortIx}
                  onChange={(e) => setFormData({...formData, sortIx: parseInt(e.target.value) || 0})}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500">
                  Höherer Wert = größerer Plan. Downgrades auf niedrigere Werte werden blockiert.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Limits</h4>
              <p className="text-xs text-gray-500">Aktivieren Sie ein Limit, um einen Maximalwert festzulegen. Deaktivierte Limits sind unbegrenzt.</p>

              {([
                { key: 'maxChatbots' as LimitFieldKey, label: 'Max. Chatbots' },
                { key: 'maxVoicebots' as LimitFieldKey, label: 'Max. Voicebots' },
                { key: 'maxCallMinutes' as LimitFieldKey, label: 'Max. Minuten/Monat' },
                { key: 'maxConversations' as LimitFieldKey, label: 'Max. Gespräche/Monat' },
                { key: 'maxTestRuns' as LimitFieldKey, label: 'Max. Testläufe' },
                { key: 'maxPhoneNumbers' as LimitFieldKey, label: 'Max. Telefonnummern' },
                { key: 'maxKnowledgeDirectories' as LimitFieldKey, label: 'Max. Knowledge Base' },
                ...(formData.externalForwardingEnabled ? [{ key: 'maxExternalForwardings' as LimitFieldKey, label: 'Max. Ext. Weiterleitungen' }] : []),
              ]).map(({ key, label }) => (
                <div key={key} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 min-w-[220px]">
                    <Switch
                      id={`${key}-toggle`}
                      checked={formData[key] !== null}
                      onCheckedChange={(checked) => {
                        setFormData({ ...formData, [key]: checked ? 1 : null });
                        if (formErrors[key]) setFormErrors({ ...formErrors, [key]: undefined });
                      }}
                    />
                    <Label htmlFor={`${key}-toggle`} className="text-sm cursor-pointer">
                      {label}
                    </Label>
                  </div>
                  {formData[key] !== null ? (
                    <div className="flex-1">
                      <Input
                        id={key}
                        type="number"
                        min={1}
                        value={formData[key] as number}
                        onChange={(e) => {
                          setFormData({ ...formData, [key]: parseInt(e.target.value) || 1 });
                          if (formErrors[key]) setFormErrors({ ...formErrors, [key]: undefined });
                        }}
                        className={`max-w-[160px] ${formErrors[key] ? 'border-red-500' : ''}`}
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Unbegrenzt</span>
                  )}
                  {formErrors[key] && (
                    <p className="text-xs text-red-500">{formErrors[key]}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
                />
                <Label htmlFor="isActive">Plan aktiv</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="isPrivate"
                  checked={formData.isPrivate}
                  onCheckedChange={(checked) => setFormData({...formData, isPrivate: checked})}
                />
                <Label htmlFor="isPrivate">Privater Plan</Label>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingPlan ? 'Speichern' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Plan endgültig löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie den Plan <strong>"{planToDelete?.name}"</strong> endgültig
              löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              className="border border-red-600 bg-transparent text-red-600 hover:bg-red-50"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Löschen...
                </>
              ) : (
                'Endgültig löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PlanToolsDialog
        planId={toolsDrawerPlan?.id ?? null}
        planName={toolsDrawerPlan?.name ?? ''}
        externalForwardingEnabled={toolsDrawerPlan?.externalForwardingEnabled}
        open={!!toolsDrawerPlan}
        onOpenChange={(open) => {
          if (!open) setToolsDrawerPlan(null);
        }}
      />

      <PlanFeaturesDialog
        planId={featuresDrawerPlan?.id ?? null}
        planName={featuresDrawerPlan?.name ?? ''}
        open={!!featuresDrawerPlan}
        onOpenChange={(open) => {
          if (!open) setFeaturesDrawerPlan(null);
        }}
      />
    </div>
  );
}
